import { useCallback, useMemo } from 'react';

import { useLocalize } from '@treams/treams-hooks';
import { MeetingFragment } from '@treams/treams-network';

const formatOptions = (format: 'date' | 'datetime' | 'time', timeZone: string) =>
	({
		timeZone,
		...(format.startsWith('date') && {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		}),
		...(format.endsWith('time') && {
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		}),
	}) satisfies Intl.DateTimeFormatOptions;

export default function useMeetingTimezone(meeting?: MeetingFragment | undefined | null) {
	const { language } = useLocalize();
	const locale = useMemo(() => language.id.replace('_', '-'), [language.id]);
	const localTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

	const meetingTimezone = useMemo(
		() => meeting?.timezone ?? localTimezone,
		[meeting?.timezone, localTimezone],
	);

	const meetingCity = useMemo(
		() => meetingTimezone?.split('/').pop()?.replace(/_/g, ' ') ?? null,
		[meetingTimezone],
	);

	const formatDate = useCallback(
		(date: Date, format: 'date' | 'datetime' | 'time', timeZone: string) => {
			return Intl.DateTimeFormat(locale, formatOptions(format, timeZone)).format(date);
		},
		[locale],
	);

	const startsAtFormatted = useMemo(() => {
		if (!meeting?.startsAt) return undefined;

		const date = new Date(meeting.startsAt);
		const format =
			formatDate(date, 'date', localTimezone) !== formatDate(date, 'date', meetingTimezone)
				? 'datetime'
				: 'time';

		return formatDate(date, format, meetingTimezone);
	}, [meeting?.startsAt, formatDate, meetingTimezone, localTimezone]);

	return {
		localTimezone,
		meetingTimezone,
		startsAtFormatted,
		meetingCity,
	};
}
